import React, { useState } from 'react'
import { Input } from '@/components/ui/input'

interface Props {
  /** Duration in decimal hours (e.g. 8.5 = 8h 30m). */
  value: number
  onChange: (value: number) => void
  minHours?: number
  maxHours?: number
  disabled?: boolean
  className?: string
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max)

export const decimalToHm = (decimalHours: number) => {
  const totalMinutes = Math.round(decimalHours * 60)
  return {
    h: Math.floor(totalMinutes / 60),
    m: totalMinutes % 60,
  }
}

export const DurationInput: React.FC<Props> = ({
  value,
  onChange,
  minHours = 0,
  maxHours = 48,
  disabled,
  className,
}) => {
  // Local string state for each field so user can type "00" or "9" freely
  // before commit-on-blur. Resync from the value prop without useEffect by
  // tracking the last-seen value and updating during render — this is the
  // React-recommended pattern for "reset state on prop change".
  const initial = decimalToHm(value)
  const [hStr, setHStr] = useState<string>(String(initial.h))
  const [mStr, setMStr] = useState<string>(String(initial.m))
  const [lastValue, setLastValue] = useState<number>(value)

  if (value !== lastValue) {
    setLastValue(value)
    const { h, m } = decimalToHm(value)
    setHStr(String(h))
    setMStr(String(m))
  }

  const commit = (rawH: string, rawM: string) => {
    const parsedH = Number(rawH || '0')
    const parsedM = clamp(Number(rawM || '0'), 0, 59)
    if (Number.isNaN(parsedH) || Number.isNaN(parsedM)) return
    const clamped = clamp(parsedH + parsedM / 60, minHours, maxHours)
    // Round to nearest minute to keep persisted values clean (no sub-minute
    // residue from float math or fractional minHours clamps).
    const totalMinutes = Math.round(clamped * 60)
    onChange(totalMinutes / 60)
  }

  return (
    <div
      className={`flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 transition-[border] focus-within:border-secondary ${
        className ?? ''
      } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      <Input
        type="number"
        min={Math.floor(minHours)}
        max={Math.floor(maxHours)}
        step={1}
        value={hStr}
        disabled={disabled}
        onChange={raw => {
          const n = raw === '' ? 0 : Number(raw)
          if (Number.isNaN(n)) return
          const clamped = clamp(n, 0, Math.floor(maxHours))
          setHStr(String(clamped))
        }}
        onBlur={() => commit(hStr, mStr)}
        className="border-0 rounded-none px-1 py-2 text-base text-right focus-visible:border-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-tertiary text-sm">h</span>
      <Input
        type="number"
        min={0}
        max={59}
        step={5}
        value={mStr}
        disabled={disabled}
        onChange={raw => {
          const n = raw === '' ? 0 : Number(raw)
          if (Number.isNaN(n)) return
          const clamped = clamp(n, 0, 59)
          setMStr(String(clamped))
        }}
        onBlur={() => commit(hStr, mStr)}
        className="border-0 rounded-none px-1 py-2 text-base text-right focus-visible:border-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-tertiary text-sm">m</span>
    </div>
  )
}
